import React, { useCallback, useEffect, useRef, useState } from "react";

import moment from "moment";

import useTimebounds from "../../../hooks/useTimebounds";
import { getTimelineMiddle, getTimelinePeriod } from "../../../utils/fast";
import {
  MultiToggleContainer,
  SelectedValueIndicator,
  ValueOption,
} from "../../multiToggle/MultiToggle";

import type { JSX } from "react";

const PREVIEW_MODE_ZOOM_OPTIONS = [
  { value: 24 * 60, label: "24h" },
  { value: 12 * 60, label: "12h", isDefaultValue: true },
  { value: 6 * 60, label: "6h" },
  { value: 3 * 60, label: "3h" },
];

const EDIT_MODE_ZOOM_OPTIONS = [
  { value: 6 * 60, label: "6h", isDefaultValue: true },
  { value: 3 * 60, label: "3h" },
  { value: 1 * 60, label: "1h" },
  { value: 60 / 2, label: "30min" },
];

interface ZoomSelectorProps {
  isEditMode: boolean;
}

export default function ZoomSelector({
  isEditMode,
}: ZoomSelectorProps): JSX.Element {
  const zoomOptionList = isEditMode
    ? EDIT_MODE_ZOOM_OPTIONS
    : PREVIEW_MODE_ZOOM_OPTIONS;

  const [selectedZoomPosition, setSelectedZoomPosition] = useState<null | {
    transform: string;
    width: number;
  }>(null);
  const zoomOptionRef = useRef<(HTMLDivElement | null)[]>([]);
  const { timelineStartDate, timelineEndDate, updateTimebounds } =
    useTimebounds();
  const [selectedZoomValue, setSelectedZoomValue] = useState<number>();

  const isEditModeRef = useRef(isEditMode); // Keep the previous value of isEditMode prop

  const updateZoom = useCallback(
    (visibleMinutes: number): void => {
      const timelineMiddle = getTimelineMiddle({
        startTime: timelineStartDate,
        endTime: timelineEndDate,
      });

      const updatedStartTime = moment(timelineMiddle)
        .subtract(visibleMinutes / 2, "minutes")
        .valueOf();

      const updatedEndTime = moment(timelineMiddle)
        .add(visibleMinutes / 2, "minutes")
        .valueOf();

      updateTimebounds({
        startTime: updatedStartTime,
        endTime: updatedEndTime,
      });
    },
    [timelineStartDate, timelineEndDate, updateTimebounds],
  );

  useEffect(() => {
    if (
      isEditModeRef.current === isEditMode &&
      selectedZoomValue &&
      selectedZoomPosition
    )
      return;

    isEditModeRef.current = isEditMode;

    const currentPeriod = getTimelinePeriod({
      startTime: timelineStartDate,
      endTime: timelineEndDate,
      unit: "minutes",
    });

    const findZoomIndex = zoomOptionList.findIndex(
      ({ value }) => value === currentPeriod,
    );

    const defaultZoomIndex = zoomOptionList.findIndex(
      ({ isDefaultValue }) => isDefaultValue,
    );

    const zoomIndex = findZoomIndex === -1 ? defaultZoomIndex : findZoomIndex;

    const currentElement = zoomOptionRef.current[zoomIndex];
    const currentZoom = zoomOptionList[zoomIndex];

    if (!currentElement || !currentZoom) return;

    updateZoom(currentZoom.value);

    setSelectedZoomPosition({
      transform: `translateX(${currentElement?.offsetLeft}px)`,
      width: currentElement?.offsetWidth || 0,
    });

    setSelectedZoomValue(currentZoom.value);
  }, [
    updateZoom,
    isEditMode,
    zoomOptionList,
    timelineStartDate,
    timelineEndDate,
    selectedZoomValue,
    selectedZoomPosition,
  ]);

  const handleZoomClick = (value: number, index: number): void => {
    const currentElement = zoomOptionRef.current[index];

    setSelectedZoomPosition({
      transform: `translateX(${Math.floor(currentElement?.offsetLeft ?? 0)}px)`,
      width: Math.floor(currentElement?.offsetWidth ?? 0),
    });

    setSelectedZoomValue(value);

    setTimeout(() => updateZoom(value));
  };

  return (
    <MultiToggleContainer>
      <SelectedValueIndicator style={selectedZoomPosition} />
      {zoomOptionList.map(({ value, label }, index) => (
        <ValueOption
          key={value}
          ref={(ref): void => {
            zoomOptionRef.current[index] = ref;
          }}
          isActive={selectedZoomValue === value}
          label={label}
          onClick={(): void => handleZoomClick(value, index)}
        />
      ))}
    </MultiToggleContainer>
  );
}
